import React, { useState } from "react";
import { message } from "antd";
import { IconPerdoruesi, IconFjalekalimi } from "../../assets/icons";
import { API } from "../../helpers/API/amplifyApi";
import { Auth } from "../../authentication/amplifyAuth";
import "./auth-scss/login.scss";
import { Form, Input, Button, Checkbox } from "antd";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../authentication/authHelpers";
import { RadarSpinner } from "react-epic-spinners";
import swal from "sweetalert";
import { ErrorLoginImage } from "../../assets/images";
import { REGULAR_USER } from "../../constantsFactory/regularUser";
import {
  ADMIN_USER,
  BRANCH_MANAGER_USER,
} from "../../constantsFactory/accessRights";
import { isBranchManager } from "../../utils/branchScope";
import {
  currentDeviceKind,
  devicePolicyErrorMessage,
  isDeviceAccessAllowed,
} from "../../utils/devicePolicy";
import { useSelector } from "react-redux";

const MFA_STEPS = new Set([
  "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
  "CONFIRM_SIGN_IN_WITH_SMS_CODE",
  "CONFIRM_SIGN_IN_WITH_EMAIL_CODE",
  "SOFTWARE_TOKEN_MFA",
  "SMS_MFA",
]);

function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useSelector((state) => state.usersList);

  const [login, setLogin] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const from = location.state?.from?.pathname || "/kryefaqja";

  const matchEmployeeSub = (email, userSub) => {
    // Fire-and-forget — do not block login on a full employees scan.
    API.get("employees", "/employees")
      .then(async (res) => {
        const norm = (s) => (s || "").toString().trim().toLowerCase();
        const toRe =
          (res || []).find((el) => el?.userSub === userSub) ||
          (email
            ? (res || []).find(
                (el) => norm(el?.employeeEmailWork) === norm(email)
              )
            : null);
        if (!toRe) return;
        await API.put("employees", `/employees/${toRe.employeeId}`, {
          body: {
            userSub,
            accountStatus: "registered",
          },
        });
      })
      .catch((e) => console.error("matchEmployeeSub failed", e));
  };

  const assertDevicePolicy = (currentUser) => {
    const policy = currentUser?.allowedDeviceTypes || "both";
    if (!isDeviceAccessAllowed(policy, currentDeviceKind())) {
      const msg = devicePolicyErrorMessage(policy);
      swal({
        title: msg,
        icon: "error",
        className: isDarkMode
          ? "custum-swal-loginError-dark"
          : "custum-swal-loginError",
      });
      Auth.signOut().catch(() => {});
      return false;
    }
    return true;
  };

  const finishSignIn = (currentUser, ErrorImg) => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    if (currentUser?.disabled === true) {
      setLoading(false);
      swal({
        title:
          "Aktualisht, ju nuk keni te drejte per aksesim! Kontaktoni me administratorin ose supervizorin perkates!",
        icon: "error",
        content: ErrorImg,
        className: isDarkMode
          ? "custum-swal-loginError-dark"
          : "custum-swal-loginError",
      });
      return;
    }
    if (!currentUser?.accessConfig) {
      setLoading(false);
      swal({
        title:
          "Profili juaj nuk është i lidhur me këtë llogari. Kontaktoni administratorin.",
        icon: "error",
        content: ErrorImg,
        className: isDarkMode
          ? "custum-swal-loginError-dark"
          : "custum-swal-loginError",
      });
      return;
    }
    if (!assertDevicePolicy(currentUser)) {
      setLoading(false);
      return;
    }
    setMfaStep(null);
    auth.signin(currentUser, () => setLoading(false));
  };

  const upsertUserFromStub = async (cognitoUser, stub) => {
    const {
      given_name,
      family_name,
      gender,
      email,
      sub,
      phone_number,
    } = cognitoUser.attributes || {};
    const all = stub?.allUsers?.Items || stub?.allUsers || [];
    const norm = (s) => (s || "").toString().trim().toLowerCase();
    const matched =
      (Array.isArray(all) &&
        (all.find((u) => u?.userSub === sub) ||
          all.find((u) => norm(u?.email) === norm(email)))) ||
      null;
    if (!matched) {
      throw new Error("no_matching_user");
    }
    const created = await API.post("users", "/users", {
      body: {
        given_name: matched.given_name || given_name,
        family_name: matched.family_name || family_name,
        gender: matched.gender || gender,
        email: matched.email || email,
        phone_number: matched.phone_number || phone_number,
        userSub: sub,
        department: matched.department,
        departmentRole: matched.departmentRole,
        userRole: matched.userRole,
        accessConfig: matched.accessConfig,
        auth2Fa: matched.auth2Fa,
        allowedDeviceTypes: matched.allowedDeviceTypes || "both",
      },
    });
    return created;
  };

  const continueAfterAuthUser = async (r) => {
    const ErrorImg = document.createElement("img");
    ErrorImg.src = ErrorLoginImage;
    await Auth.currentCredentials();
    await API.get("users", "/users")
      .then(async (res) => {
        let currentUser = res;
        if (currentUser && !currentUser.accessConfig) {
          try {
            currentUser = await upsertUserFromStub(r, res);
          } catch (e) {
            console.error("upsertUserFromStub failed", e);
            finishSignIn(null, ErrorImg);
            return;
          }
        }
        finishSignIn(currentUser, ErrorImg);
      })
      .catch(async () => {
        const {
          given_name,
          family_name,
          gender,
          email,
          sub,
          phone_number,
        } = r.attributes || {};
        try {
          const created = await API.post("users", "/users", {
            body: {
              given_name,
              family_name,
              gender,
              email,
              phone_number,
              userSub: sub,
              department: r.attributes?.["custom:department"],
              departmentRole: r.attributes?.["custom:role"],
              userRole: r.attributes?.["custom:userRole"],
              accessConfig: (() => {
                const roleAttr = r.attributes?.["custom:userRole"];
                if (isBranchManager(roleAttr)) {
                  return BRANCH_MANAGER_USER.accessConfig;
                }
                if (roleAttr === "Admin" || roleAttr === "Super Admin") {
                  return ADMIN_USER.accessConfig;
                }
                return REGULAR_USER.accessConfig;
              })(),
            },
          });
          matchEmployeeSub(email, sub);
          finishSignIn(created, ErrorImg);
        } catch (e) {
          console.error("create user on login failed", e);
          finishSignIn(null, ErrorImg);
        }
      });
  };

  const onFinish = async (values) => {
    setLoading(true);
    const ErrorImg = document.createElement("img");
    ErrorImg.src = ErrorLoginImage;
    try {
      const r = await Auth.signIn(values.username, values.password);
      await continueAfterAuthUser(r);
    } catch (err) {
      const step = err?.code || err?.nextStep?.signInStep;
      if (err?.name === "SignInNextStep" && MFA_STEPS.has(step)) {
        setMfaStep(step);
        setLoading(false);
        message.info("Vendosni kodin MFA nga aplikacioni authenticator.");
        return;
      }
      setLoading(false);
      swal({
        title: "Kredenciale te gabuara!",
        icon: "error",
        content: ErrorImg,
        className: isDarkMode
          ? "custum-swal-loginError-dark"
          : "custum-swal-loginError",
      });
      console.log("Error login", err);
    }
  };

  const onConfirmMfa = async () => {
    if (!mfaCode.trim()) {
      message.warning("Vendosni kodin MFA");
      return;
    }
    setLoading(true);
    const ErrorImg = document.createElement("img");
    ErrorImg.src = ErrorLoginImage;
    try {
      await Auth.confirmSignIn(null, mfaCode.trim());
      const r = await Auth.currentAuthenticatedUser();
      await continueAfterAuthUser(r);
    } catch (err) {
      setLoading(false);
      swal({
        title: "Kodi MFA është i gabuar!",
        icon: "error",
        content: ErrorImg,
        className: isDarkMode
          ? "custum-swal-loginError-dark"
          : "custum-swal-loginError",
      });
      console.log("MFA confirm failed", err);
    }
  };

  return (
    <div className="login-grid">
      {!mfaStep ? (
        <Form
          name="normal_login"
          className="login-form"
          initialValues={{ remember: true }}
          onFinish={onFinish}
        >
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: "Ju lutem shkruani E-mailin tuaj !",
              },
            ]}
          >
            <Input
              size="large"
              placeholder="Emri i Përdoruesit"
              autoComplete="username"
              suffix={<IconPerdoruesi width={16} height={16} fill="#0288d2" />}
              onChange={(e) => {
                setLogin((prev) => ({
                  ...prev,
                  username: e.target.value,
                }));
              }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: "Ju lutem shkruani fjalëkalimin !",
              },
            ]}
          >
            <Input.Password
              size="large"
              type="password"
              placeholder="Fjalëkalimi"
              autoComplete="current-password"
              iconRender={() => (
                <IconFjalekalimi width={16} height={16} fill="#0288d2" />
              )}
              onChange={(e) => {
                setLogin((prev) => ({
                  ...prev,
                  password: e.target.value,
                }));
              }}
            />
          </Form.Item>
          <div className="custom-form">
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>Më mbaj mend!</Checkbox>
            </Form.Item>
            <Link className="login-form-forgot" to="/harruatkodin">
              Ke harruar fjalëkalimin?
            </Link>
          </div>
          <Form.Item>
            <Button
              type="primary"
              className={
                login.username ? "filled-button" : "login-form-button"
              }
              htmlType="submit"
              disabled={loading}
              block
            >
              Futu në adresën tënde!
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div className="login-mfa">
          <p className="login-mfa__hint">
            Vendosni kodin 6-shifror nga aplikacioni MFA.
          </p>
          <Input
            size="large"
            placeholder="Kodi MFA"
            value={mfaCode}
            maxLength={8}
            onChange={(e) => setMfaCode(e.target.value)}
            onPressEnter={onConfirmMfa}
          />
          <Button
            type="primary"
            className="filled-button"
            onClick={onConfirmMfa}
            disabled={loading}
            block
            style={{ marginTop: 16 }}
          >
            Konfirmo MFA
          </Button>
          <Button
            type="link"
            onClick={() => {
              setMfaStep(null);
              setMfaCode("");
              Auth.signOut().catch(() => {});
            }}
          >
            ← Kthehu te login
          </Button>
        </div>
      )}
      <div className="loading-login" aria-live="polite">
        {loading ? <RadarSpinner size={40} color="#1da193" /> : null}
      </div>
    </div>
  );
}
export default Login;
